import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import AttendanceNavigation from "../attendance/AttendanceNavigation";
import {
	JOB_GROUP_ORDER,
	buildJobGroupRowSpans,
	getAttendanceJobGroup,
} from "../attendance/attendanceHelpers";
import {
	Button,
	Card,
	Checkbox,
	ConfigProvider,
	DatePicker,
	Form,
	Input,
	Layout,
	Modal,
	Popconfirm,
	Radio,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	Upload,
	message,
} from "antd";
import {
	PlusOutlined,
	UploadOutlined,
	DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import locale from "antd/locale/ko_KR";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

const { Content } = Layout;

const WEEKDAY_OPTIONS = [
	{ value: 6, label: "일" },
	{ value: 0, label: "월" },
	{ value: 1, label: "화" },
	{ value: 2, label: "수" },
	{ value: 3, label: "목" },
	{ value: 4, label: "금" },
	{ value: 5, label: "토" },
];

const STATUS_OPTIONS = [
	{ value: "근무 중", label: "근무 중" },
	{ value: "휴직 중", label: "휴직 중" },
	{ value: "퇴사", label: "퇴사" },
];

const weekdayLabel = (days) => {
	if (!days || !days.length) return "-";
	const map = Object.fromEntries(
		WEEKDAY_OPTIONS.map((d) => [d.value, d.label]),
	);
	return WEEKDAY_OPTIONS.filter((d) => days.includes(d.value))
		.map((d) => map[d.value])
		.join(", ");
};

const sameWeeklyOffDays = (a = [], b = []) => {
	const left = [...(a || [])].sort((x, y) => x - y);
	const right = [...(b || [])].sort((x, y) => x - y);
	if (left.length !== right.length) return false;
	return left.every((value, index) => value === right[index]);
};

const ManageEmployeeInfo = () => {
	const isMobile = useIsMobile();
	const [rows, setRows] = useState([]);
	const [staffOptions, setStaffOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [resultOpen, setResultOpen] = useState(false);
	const [uploadResult, setUploadResult] = useState(null);
	const [nameSearch, setNameSearch] = useState("");
	const [sortMode, setSortMode] = useState("name");
	const [resignOpen, setResignOpen] = useState(false);
	const [resignTarget, setResignTarget] = useState(null);
	const [resignSaving, setResignSaving] = useState(false);
	const [form] = Form.useForm();
	const [resignForm] = Form.useForm();
	const watchedWeeklyOffDays = Form.useWatch("weekly_off_days", form);

	const weeklyOffChanged = Boolean(
		editing &&
		!sameWeeklyOffDays(
			watchedWeeklyOffDays || [],
			editing.weekly_off_days || [],
		),
	);

	const loadRows = useCallback(async () => {
		try {
			setLoading(true);
			const res = await useAxios.get("/users/employee-info/");
			setRows(res.data || []);
		} catch {
			message.error("교직원 정보를 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, []);

	const loadStaff = useCallback(async () => {
		try {
			const res = await useAxios.get("/users/clergy-staff/");
			setStaffOptions(res.data || []);
		} catch {
			setStaffOptions([]);
		}
	}, []);

	useEffect(() => {
		loadRows();
		loadStaff();
	}, [loadRows, loadStaff]);

	const usedUserIds = useMemo(
		() =>
			new Set(
				rows
					.map((row) => row.user_id)
					.filter(
						(id) => id !== editing?.user_id,
					),
			),
		[rows, editing],
	);

	const userSelectOptions = staffOptions
		.filter(
			(user) =>
				!usedUserIds.has(user.id) ||
				user.id === editing?.user_id,
		)
		.map((user) => ({
			value: user.id,
			label: `${user.name}${user.job ? ` · ${user.job}` : ""}`,
		}));

	const openCreate = () => {
		setEditing(null);
		form.resetFields();
		form.setFieldsValue({
			employment_status: "근무 중",
			weekly_off_days: [],
			sort_at_bottom: false,
			is_home_church: true,
		});
		setModalOpen(true);
	};

	const openEdit = (record) => {
		setEditing(record);
		form.setFieldsValue({
			user_id: record.user_id,
			attendance_code: record.attendance_code,
			hire_date: record.hire_date
				? dayjs(record.hire_date)
				: null,
			employment_status: record.employment_status,
			weekly_off_days: record.weekly_off_days || [],
			weekly_off_effective_from: null,
			sort_at_bottom: Boolean(record.sort_at_bottom),
			is_home_church: record.is_home_church !== false,
			resign_date: record.resign_date
				? dayjs(record.resign_date)
				: null,
		});
		setModalOpen(true);
	};

	const handleSave = async () => {
		try {
			const values = await form.validateFields();
			const nextDays = values.weekly_off_days || [];
			const daysChanged =
				editing &&
				!sameWeeklyOffDays(
					nextDays,
					editing.weekly_off_days || [],
				);
			const payload = {
				user_id: values.user_id,
				attendance_code: values.attendance_code,
				hire_date: values.hire_date.format(
					"YYYY-MM-DD",
				),
				employment_status: values.employment_status,
				weekly_off_days: nextDays,
				sort_at_bottom: Boolean(values.sort_at_bottom),
				is_home_church: Boolean(values.is_home_church),
				resign_date: values.resign_date
					? values.resign_date.format(
							"YYYY-MM-DD",
						)
					: null,
			};
			if (!editing) {
				payload.show_attendance = true;
			}
			if (daysChanged) {
				payload.weekly_off_effective_from =
					values.weekly_off_effective_from.format(
						"YYYY-MM-DD",
					);
			}
			if (editing) {
				await useAxios.put(
					`/users/employee-info/${editing.id}/`,
					payload,
				);
				message.success(
					"교직원 정보가 수정되었습니다.",
				);
			} else {
				await useAxios.post(
					"/users/employee-info/",
					payload,
				);
				message.success(
					"교직원 정보가 추가되었습니다.",
				);
			}
			setModalOpen(false);
			loadRows();
		} catch (err) {
			if (err?.errorFields) return;
			const apiError =
				err?.response?.data?.user_id?.[0] ||
				err?.response?.data?.attendance_code?.[0] ||
				err?.response?.data?.resign_date?.[0] ||
				err?.response?.data
					?.weekly_off_effective_from?.[0];
			message.error(apiError || "저장에 실패했습니다.");
		}
	};

	const handleDelete = async (record) => {
		try {
			await useAxios.delete(
				`/users/employee-info/${record.id}/`,
			);
			message.success("삭제되었습니다.");
			loadRows();
		} catch {
			message.error("삭제에 실패했습니다.");
		}
	};

	const openResign = (record) => {
		setResignTarget(record);
		resignForm.setFieldsValue({
			resign_date: record.resign_date
				? dayjs(record.resign_date)
				: dayjs(),
		});
		setResignOpen(true);
	};

	const submitResign = async () => {
		if (!resignTarget) {
			return;
		}
		try {
			const values = await resignForm.validateFields();
			setResignSaving(true);
			await useAxios.put(
				`/users/employee-info/${resignTarget.id}/`,
				{
					resign_date: values.resign_date.format(
						"YYYY-MM-DD",
					),
					employment_status: "퇴사",
				},
			);
			message.success("퇴사 처리했습니다.");
			setResignOpen(false);
			setResignTarget(null);
			loadRows();
		} catch (err) {
			if (err?.errorFields) {
				return;
			}
			message.error(
				err?.response?.data?.resign_date?.[0] ||
					err?.response?.data?.error ||
					"퇴사 처리에 실패했습니다.",
			);
		} finally {
			setResignSaving(false);
		}
	};

	const cancelResign = (record) => {
		Modal.confirm({
			title: "퇴사를 취소할까요?",
			content: `${record.user_name}의 상태를 근무 중으로 되돌립니다.`,
			okText: "퇴사 취소",
			cancelText: "닫기",
			onOk: async () => {
				try {
					await useAxios.put(
						`/users/employee-info/${record.id}/`,
						{
							resign_date: null,
							employment_status: "근무 중",
						},
					);
					message.success("퇴사를 취소했습니다.");
					loadRows();
				} catch (err) {
					message.error(
						err?.response?.data?.error ||
							"퇴사 취소에 실패했습니다.",
					);
				}
			},
		});
	};

	const downloadTemplate = async () => {
		try {
			const res = await useAxios.get(
				"/users/employee-info/upload-template/",
				{ responseType: "blob" },
			);
			const url = window.URL.createObjectURL(res.data);
			const link = document.createElement("a");
			link.href = url;
			link.download = "교직원_정보_업로드_양식.xlsx";
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			message.error("양식 파일을 받지 못했습니다.");
		}
	};

	const handleExcelUpload = async (file) => {
		if (
			!String(file.name || "")
				.toLowerCase()
				.endsWith(".xlsx")
		) {
			message.error(
				".xlsx 엑셀 파일만 업로드할 수 있습니다.",
			);
			return false;
		}
		const formData = new FormData();
		formData.append("file", file);
		setUploading(true);
		try {
			const res = await useAxios.post(
				"/users/employee-info/bulk-upload/",
				formData,
				{
					transformRequest: [
						(data, headers) => {
							delete headers[
								"Content-Type"
							];
							return data;
						},
					],
				},
			);
			setUploadResult(res.data);
			setResultOpen(true);
			message.success(
				`업로드 완료 (추가 ${res.data.created}건, 수정 ${res.data.updated}건)`,
			);
			loadRows();
		} catch (err) {
			const data = err?.response?.data;
			if (data?.errors) {
				setUploadResult(data);
				setResultOpen(true);
				message.error(
					"오류가 있어 저장하지 않았습니다. 내용을 확인하세요.",
				);
			} else {
				message.error(
					data?.error ||
						"엑셀 업로드에 실패했습니다.",
				);
			}
		} finally {
			setUploading(false);
		}
		return false;
	};

	const displayedRows = useMemo(() => {
		const keyword = nameSearch.trim().toLowerCase();
		let list = rows;
		if (keyword) {
			list = list.filter((row) =>
				String(row.user_name || "")
					.toLowerCase()
					.includes(keyword),
			);
		}
		const sorted = [...list];
		if (sortMode === "job") {
			sorted.sort((a, b) => {
				const ga = getAttendanceJobGroup(a);
				const gb = getAttendanceJobGroup(b);
				const ia = JOB_GROUP_ORDER.indexOf(ga);
				const ib = JOB_GROUP_ORDER.indexOf(gb);
				if (ia !== ib) return ia - ib;
				return String(a.user_name || "").localeCompare(
					String(b.user_name || ""),
					"ko",
				);
			});
		} else {
			sorted.sort((a, b) =>
				String(a.user_name || "").localeCompare(
					String(b.user_name || ""),
					"ko",
				),
			);
		}
		return sorted;
	}, [rows, nameSearch, sortMode]);

	const groupSpans = useMemo(
		() =>
			sortMode === "job"
				? buildJobGroupRowSpans(displayedRows)
				: [],
		[displayedRows, sortMode],
	);

	const toggleShowAttendance = async (record, checked) => {
		const previous = record.show_attendance !== false;
		setRows((prev) =>
			prev.map((row) =>
				row.id === record.id
					? { ...row, show_attendance: checked }
					: row,
			),
		);
		try {
			await useAxios.put(
				`/users/employee-info/${record.id}/`,
				{
					show_attendance: checked,
				},
			);
			message.success(
				checked
					? "근태현황에 표시합니다."
					: "근태현황에서 숨겼습니다.",
			);
		} catch {
			setRows((prev) =>
				prev.map((row) =>
					row.id === record.id
						? {
								...row,
								show_attendance:
									previous,
							}
						: row,
				),
			);
			message.error("근태현황 표시 변경에 실패했습니다.");
		}
	};

	const toggleHomeChurch = async (record, checked) => {
		const previous = record.is_home_church !== false;
		setRows((prev) =>
			prev.map((row) =>
				row.id === record.id
					? { ...row, is_home_church: checked }
					: row,
			),
		);
		try {
			await useAxios.put(
				`/users/employee-info/${record.id}/`,
				{
					is_home_church: checked,
				},
			);
			message.success(
				checked
					? "본교회 소속으로 표시했습니다."
					: "본교회 소속에서 제외했습니다. 연차 사용촉진 대상에서도 빠집니다.",
			);
		} catch {
			setRows((prev) =>
				prev.map((row) =>
					row.id === record.id
						? {
								...row,
								is_home_church:
									previous,
							}
						: row,
				),
			);
			message.error("본교회 소속 변경에 실패했습니다.");
		}
	};

	const columns = [
		...(sortMode === "job"
			? [
					{
						title: "소속",
						key: "job_group",
						width: 88,
						onCell: (_, index) => ({
							rowSpan:
								groupSpans[
									index
								] || 0,
						}),
						render: (_, row) => (
							<span className="leave-balance__group-label">
								{getAttendanceJobGroup(
									row,
								)}
							</span>
						),
					},
				]
			: []),
		{ title: "이름", dataIndex: "user_name", width: 120 },
		{ title: "직분", dataIndex: "classification_name", width: 100 },
		{
			title: sortMode === "job" ? "팀" : "소속",
			dataIndex: "employee_team_name",
			width: 120,
			render: (v) => v || "-",
		},
		{ title: "출근코드", dataIndex: "attendance_code", width: 110 },
		{ title: "입사일", dataIndex: "hire_date", width: 120 },
		{
			title: "상태",
			dataIndex: "employment_status",
			width: 100,
			render: (value) =>
				value === "퇴사" ? (
					<Tag color="default">퇴사</Tag>
				) : (
					value || "-"
				),
		},
		{
			title: "정기휴무",
			dataIndex: "weekly_off_days",
			render: (days) => weekdayLabel(days),
		},
		{
			title: "본교회 소속",
			dataIndex: "is_home_church",
			width: 110,
			render: (value, record) => (
				<Switch
					checked={value !== false}
					checkedChildren="예"
					unCheckedChildren="아니오"
					onChange={(checked) =>
						toggleHomeChurch(
							record,
							checked,
						)
					}
				/>
			),
		},
		{
			title: "근태현황",
			dataIndex: "show_attendance",
			width: 100,
			render: (value, record) => (
				<Switch
					checked={value !== false}
					checkedChildren="표시"
					unCheckedChildren="숨김"
					onChange={(checked) =>
						toggleShowAttendance(
							record,
							checked,
						)
					}
				/>
			),
		},
		{
			title: "하단 정렬",
			dataIndex: "sort_at_bottom",
			width: 100,
			render: (value) => (value ? "예" : "-"),
		},

		{
			title: "작업",
			key: "actions",
			width: 220,
			render: (_, record) => (
				<Space wrap>
					<Button
						type="link"
						onClick={() =>
							openEdit(record)
						}>
						수정
					</Button>
					{record.employment_status ===
					"퇴사" ? (
						<Button
							type="link"
							onClick={() =>
								cancelResign(record)
							}>
							퇴사 취소
						</Button>
					) : (
						<Button
							type="link"
							danger
							onClick={() =>
								openResign(record)
							}>
							퇴사
						</Button>
					)}
					<Popconfirm
						title="이 교직원 정보를 삭제할까요?"
						okText="삭제"
						cancelText="취소"
						onConfirm={() =>
							handleDelete(record)
						}>
						<Button type="link" danger>
							삭제
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="교직원 근태 정보 설정" />
			<Layout className="member-layout">
				<AttendanceNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="page">
							<PageHeader
								title="교직원 근태 정보 설정"
							/>
							<Card className="surface-card">
								<div
									style={{
										marginBottom: 16,
										display: "flex",
										justifyContent:
											"space-between",
										gap: 8,
										flexWrap: "wrap",
										alignItems: "center",
									}}>
									<Space wrap>
										<Input.Search
											allowClear
											placeholder="이름 검색"
											value={
												nameSearch
											}
											onChange={(
												e,
											) =>
												setNameSearch(
													e
														.target
														.value,
												)
											}
											style={{
												width: 220,
											}}
										/>
										<Radio.Group
											value={
												sortMode
											}
											onChange={(
												e,
											) =>
												setSortMode(
													e
														.target
														.value,
												)
											}
											optionType="button"
											options={[
												{
													label: "가나다 순",
													value: "name",
												},
												{
													label: "소속 순",
													value: "job",
												},
											]}
										/>
									</Space>
									<Space wrap>
										<Button
											icon={
												<DownloadOutlined />
											}
											onClick={
												downloadTemplate
											}>
											엑셀
											양식
											다운로드
										</Button>
										<Upload
											accept=".xlsx"
											showUploadList={
												false
											}
											beforeUpload={
												handleExcelUpload
											}>
											<Button
												icon={
													<UploadOutlined />
												}
												loading={
													uploading
												}>
												엑셀
												업로드
											</Button>
										</Upload>
										<Button
											type="primary"
											icon={
												<PlusOutlined />
											}
											onClick={
												openCreate
											}>
											교직원
											추가
										</Button>
									</Space>
								</div>
								<Table
									rowKey="id"
									columns={
										columns
									}
									dataSource={
										displayedRows
									}
									loading={
										loading
									}
									pagination={
										false
									}
								/>
							</Card>
						</div>
					</Content>
				</Layout>
			</Layout>

			<Modal
				title={
					editing
						? "교직원 정보 수정"
						: "교직원 정보 추가"
				}
				open={modalOpen}
				onOk={handleSave}
				onCancel={() => setModalOpen(false)}
				okText="저장"
				cancelText="취소"
				destroyOnClose>
				<Form form={form} layout="vertical">
					<Form.Item
						name="user_id"
						label="사용자"
						rules={[
							{
								required: true,
								message: "사용자를 선택하세요",
							},
						]}>
						<Select
							showSearch
							optionFilterProp="label"
							options={
								userSelectOptions
							}
							placeholder="교역자/교직원 검색"
							disabled={Boolean(
								editing,
							)}
						/>
					</Form.Item>
					<Form.Item
						name="attendance_code"
						label="출근코드"
						rules={[
							{
								required: true,
								message: "출근코드를 입력하세요",
							},
						]}>
						<Input placeholder="예: 0188" />
					</Form.Item>
					<Form.Item
						name="hire_date"
						label="입사일"
						rules={[
							{
								required: true,
								message: "입사일을 선택하세요",
							},
						]}>
						<DatePicker
							inputReadOnly={isMobile}
							style={{
								width: "100%",
							}}
						/>
					</Form.Item>
					<Form.Item
						name="employment_status"
						label="상태"
						rules={[
							{
								required: true,
								message: "상태를 선택하세요",
							},
						]}>
						<Select
							options={STATUS_OPTIONS}
						/>
					</Form.Item>
					<Form.Item
						name="weekly_off_days"
						label="정기휴무 요일">
						<Checkbox.Group
							options={
								WEEKDAY_OPTIONS
							}
						/>
					</Form.Item>
					{editing ? (
						<Form.Item
							name="weekly_off_effective_from"
							label="정기휴무 적용 시작일"
							extra="요일을 바꿀 때만 필요합니다. 이 날짜부터 새 요일이 적용되고, 이전 날짜는 기존 이력을 유지합니다."
							rules={
								weeklyOffChanged
									? [
											{
												required: true,
												message: "적용 시작일을 선택하세요",
											},
										]
									: []
							}>
							<DatePicker
								inputReadOnly={isMobile}
								style={{
									width: "100%",
								}}
								disabled={
									!weeklyOffChanged
								}
								placeholder={
									weeklyOffChanged
										? "적용 시작일"
										: "요일을 변경하면 선택합니다"
								}
							/>
						</Form.Item>
					) : null}
					{editing?.weekly_off_periods?.length ? (
						<div
							style={{
								marginBottom: 16,
							}}>
							<div
								style={{
									marginBottom: 8,
									fontWeight: 600,
									color: "rgba(0,0,0,0.65)",
								}}>
								정기휴무 적용
								이력
							</div>
							<Table
								size="small"
								pagination={
									false
								}
								rowKey={(row) =>
									row.id ||
									row.effective_from
								}
								dataSource={
									editing.weekly_off_periods
								}
								columns={[
									{
										title: "적용 시작일",
										dataIndex: "effective_from",
										width: 130,
									},
									{
										title: "요일",
										dataIndex: "weekly_off_days",
										render: (
											days,
										) =>
											weekdayLabel(
												days,
											),
									},
								]}
							/>
						</div>
					) : null}
					<Form.Item
						name="is_home_church"
						label="본교회 소속"
						valuePropName="checked">
						<Checkbox>
							본교회 소속 (끄면 연차
							사용촉진 대상에서 제외)
						</Checkbox>
					</Form.Item>
					<Form.Item
						name="sort_at_bottom"
						label="근태 하단 정렬"
						valuePropName="checked">
						<Checkbox>
							직분과 관계없이 근태
							목록 맨 아래(기타)에
							표시
						</Checkbox>
					</Form.Item>
					<Form.Item
						name="resign_date"
						label="퇴사일">
						<DatePicker
							inputReadOnly={isMobile}
							style={{
								width: "100%",
							}}
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="엑셀 업로드 결과"
				open={resultOpen}
				onCancel={() => setResultOpen(false)}
				onOk={() => setResultOpen(false)}
				okText="확인"
				cancelButtonProps={{
					style: { display: "none" },
				}}>
				{uploadResult?.errors?.length ? (
					<>
						<p>
							오류가 있어 아무 것도
							저장하지 않았습니다.
							아래 행을 수정한 뒤 다시
							업로드하세요.
						</p>
						<Table
							size="small"
							rowKey={(row) =>
								`${row.row}-${row.message}`
							}
							pagination={false}
							dataSource={
								uploadResult.errors
							}
							columns={[
								{
									title: "행",
									dataIndex: "row",
									width: 70,
								},
								{
									title: "이름",
									dataIndex: "name",
									width: 100,
								},
								{
									title: "내용",
									dataIndex: "message",
								},
							]}
						/>
					</>
				) : (
					<p>
						추가{" "}
						{uploadResult?.created ?? 0}건,
						수정{" "}
						{uploadResult?.updated ?? 0}건이
						반영되었습니다.
					</p>
				)}
			</Modal>
			<Modal
				title={
					resignTarget
						? `${resignTarget.user_name} 퇴사 처리`
						: "퇴사 처리"
				}
				open={resignOpen}
				onCancel={() => {
					setResignOpen(false);
					setResignTarget(null);
				}}
				onOk={submitResign}
				okText="퇴사"
				okButtonProps={{ danger: true }}
				cancelText="닫기"
				confirmLoading={resignSaving}
				destroyOnClose>
				<Form
					form={resignForm}
					layout="vertical"
					style={{ marginTop: 8 }}>
					<Form.Item
						name="resign_date"
						label="퇴사일"
						rules={[
							{
								required: true,
								message: "퇴사일을 선택하세요",
							},
						]}>
						<DatePicker
							inputReadOnly={isMobile}
							style={{ width: "100%" }}
							disabledDate={(current) => {
								if (!current || !resignTarget?.hire_date) {
									return false;
								}
								return current.isBefore(
									dayjs(resignTarget.hire_date),
									"day",
								);
							}}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</ConfigProvider>
	);
};

export default ManageEmployeeInfo;
